import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Autocomplete, Box, Button, Card, Divider, IconButton, TextField, Typography } from '@mui/material';
import { blue, grey, red } from '@mui/material/colors';
import { Handle, Position, useConnection, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import React, { ReactNode, useState } from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { Controller, FieldValues, UseFormReturn, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useBeforeUnload } from 'react-router-dom';
// @ts-ignore
import { v4 as uuid } from 'uuid';

import OberonDialog from '~components/OberonDialog';
import { StrictModeDroppable } from '~components/StrictModeDroppable';
import { CompareOp, ContactFlowParameter, ParameterClass } from '~pages/SipConfiguration/domain';

import { SelectorTypes } from '../../Selectors';
import BaseSelector from '../../Selectors/BaseSelector';
import DefaultSelector from '../../Selectors/DefaultSelector';
import { ContactFlowNode, ContactFlowNodeData, ContactFlowNodeProps } from '..';

export type BaseNodeProps<T extends FieldValues> = Pick<ContactFlowNodeProps, 'id' | 'data'> & {
  title: string;
} & (
    | {
        parameterForm?: undefined;
        renderParameter?: (parameter: ContactFlowParameter) => ReactNode;
      }
    | {
        parameterForm: UseFormReturn<T>;
        renderParameterForm?: (
          data: UseFormReturn<T>['formState']['defaultValues'],
          parameter: ContactFlowParameter,
        ) => ReactNode;
        renderEditParameter?: (
          form: UseFormReturn<T>,
          { parameter, isEdit }: { parameter: ContactFlowParameter; isEdit: boolean },
        ) => ReactNode;
        transformParameter: (data: T) => ContactFlowParameter;
      }
  );

export default function BaseNode<T extends FieldValues>(props: BaseNodeProps<T>) {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { getHandleConnections, updateNode, deleteElements, getEdges } = useReactFlow<ContactFlowNode>();
  const updateNodeInternals = useUpdateNodeInternals();
  const conn = useConnection();

  const { id, title, data, parameterForm } = props;
  const { type, description, parameter, sourceHandles: initialSourceHandles, isNew } = data;

  const form = useForm<Omit<ContactFlowNodeData, 'type' | 'parameter'>>({
    defaultValues: data,
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty, submitCount },
    control,
    setValue,
  } = form;
  const {
    fields: sourceHandles,
    append: appendSourceHandle,
    remove: removeSourceHandle,
    move: moveSourceHandle,
  } = useFieldArray({
    control,
    name: 'sourceHandles',
  });

  const isClean = !(isDirty || parameterForm?.formState.isDirty);
  const isModified = submitCount > 0;

  useBeforeUnload((e) => {
    if (!isClean) {
      e.preventDefault();

      // Included for legacy support, e.g. Chrome/Edge < 119
      e.returnValue = true;
    }
  });

  const resetAll = () => {
    reset(undefined, { keepSubmitCount: true });
    parameterForm?.reset(undefined, { keepSubmitCount: true });
  };
  const onClose = () => {
    setIsEdit(false);
  };
  const toggleEdit = () => setIsEdit((p) => !p);

  return (
    <>
      <OberonDialog
        open={isEdit}
        title={title}
        subHeader={`ID: ${id}`}
        onClose={onClose}
        onSubmit={handleSubmit((values) => {
          const data: ContactFlowNodeData = {
            ...values,
            type,
            parameter,
          };
          parameterForm?.handleSubmit((parameter) => {
            data.parameter = props.transformParameter(parameter);
            parameterForm.reset(parameter, { keepSubmitCount: true });
          })();

          const handlesToBeRemoved = initialSourceHandles?.filter(
            (o) => !values.sourceHandles?.some((v) => v.handleId === o.handleId),
          );
          deleteElements({
            edges: getEdges().filter((edge) =>
              handlesToBeRemoved?.some((handle) => handle.handleId === edge.sourceHandle),
            ),
          });
          updateNode(id, {
            data,
          });
          updateNodeInternals(id);
          reset(values, { keepSubmitCount: true });
          setIsEdit(false);
        })}
        content={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
              <Controller
                control={control}
                name='description'
                defaultValue=''
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    size='small'
                    variant='outlined'
                    label='Description'
                  />
                )}
              />
            </Box>
            {parameterForm && props.renderEditParameter?.(parameterForm, { parameter, isEdit })}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
              <Typography sx={{ flexGrow: 1 }}>Next Routes</Typography>
              {sourceHandles.length > 0 && (
                <DragDropContext
                  onDragEnd={(e) => {
                    if (!e.destination) {
                      return;
                    }
                    const fromIndex = e.source.index;
                    const toIndex = e.destination?.index;

                    if (toIndex == fromIndex) {
                      return;
                    }
                    moveSourceHandle(fromIndex, toIndex);
                  }}>
                  <StrictModeDroppable droppableId={`droppable-${id}`}>
                    {(provided) => (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                        {...provided.droppableProps}
                        ref={provided.innerRef}>
                        {sourceHandles.map(({ id, handleId }, i) => {
                          return (
                            <Draggable key={id} draggableId={id} index={i}>
                              {(provided) => {
                                const selector = useWatch({
                                  control,
                                  name: `sourceHandles.${i}.selector`,
                                });
                                return (
                                  <Card
                                    key={id}
                                    variant='outlined'
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                    }}
                                    {...provided.draggableProps}
                                    ref={provided.innerRef}>
                                    <IconButton
                                      sx={{
                                        ':hover': {
                                          background: 'none',
                                          backgroundColor: 'none',
                                        },
                                      }}
                                      disableRipple
                                      disableFocusRipple
                                      disableTouchRipple
                                      {...provided.dragHandleProps}>
                                      <DragIndicatorIcon />
                                    </IconButton>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        paddingTop: 2,
                                        paddingRight: 2,
                                        paddingBottom: 2,
                                        width: '100%',
                                      }}>
                                      <Autocomplete
                                        fullWidth
                                        options={[...Object.values(ParameterClass)]}
                                        renderInput={(params) => <TextField {...params} label='Selector Type' />}
                                        value={selector?.class || null}
                                        onChange={(e, v) => {
                                          if (v) {
                                            setValue(`sourceHandles.${i}.selector.class`, v, { shouldDirty: true });
                                          } else {
                                            setValue(`sourceHandles.${i}`, { handleId }, { shouldDirty: true });
                                          }
                                        }}
                                      />
                                      {/* TODO: custom selector component */}
                                      {selector?.class && (
                                        <>
                                          <Controller
                                            control={control}
                                            name={`sourceHandles.${i}.selector.field`}
                                            defaultValue=''
                                            render={({ field }) => <TextField {...field} fullWidth label='Field' />}
                                          />
                                          <Controller
                                            control={control}
                                            name={`sourceHandles.${i}.selector.operation`}
                                            render={({ field: { value, onChange, ...rest } }) => (
                                              <Autocomplete
                                                {...rest}
                                                fullWidth
                                                options={[...Object.values(CompareOp)]}
                                                renderInput={(params) => <TextField {...params} label='Operation' />}
                                                value={value || null}
                                                onChange={(e, v) => onChange(v)}
                                              />
                                            )}
                                          />
                                          <Controller
                                            control={control}
                                            name={`sourceHandles.${i}.selector.value`}
                                            defaultValue=''
                                            render={({ field }) => <TextField {...field} fullWidth label='Value' />}
                                          />
                                        </>
                                      )}
                                      <Button
                                        variant='contained'
                                        disableElevation
                                        color='error'
                                        sx={{ alignSelf: 'end' }}
                                        onClick={() => {
                                          removeSourceHandle(i);
                                        }}>
                                        Remove
                                      </Button>
                                    </Box>
                                  </Card>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </StrictModeDroppable>
                </DragDropContext>
              )}
              <Button
                variant='contained'
                disableElevation
                fullWidth
                color='primary'
                startIcon={<AddIcon />}
                onClick={() => appendSourceHandle({ handleId: `handle-${id}-${uuid()}` })}>
                Add route
              </Button>
            </Box>
          </Box>
        }
        actionFooter={
          <>
            <Button variant='text' onClick={onClose}>
              Close
            </Button>
            <Button
              variant='text'
              disableElevation
              color='error'
              onClick={() => {
                deleteElements({
                  nodes: [{ id }],
                });
              }}>
              Delete
            </Button>
            <Button variant='text' disableElevation color='error' onClick={() => resetAll()} disabled={isClean}>
              Reset
            </Button>
            <Button type='submit' variant='contained' disableElevation color='primary' disabled={isClean}>
              Save
            </Button>
          </>
        }
      />
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: isClean
            ? isNew || isModified
              ? `2px solid ${red[600]}`
              : '1px solid black'
            : `2px dashed ${red[600]}`,
          minWidth: 200,
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}>
        <Box
          sx={{
            paddingTop: 0.5,
            paddingBottom: 0.5,
            paddingLeft: 2,
            paddingRight: 2,
            borderBottom: '1px solid black',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: grey[900],
            color: '#ffffff',
            position: 'relative',
          }}>
          <Handle
            id={id}
            type='target'
            position={Position.Left}
            style={{
              width: 14,
              height: 14,
              background: (
                conn.inProgress
                  ? conn.fromHandle.id === id || (conn.fromHandle.type == 'source' && conn.fromNode.id !== id)
                  : true
              )
                ? blue[600]
                : grey[500],
            }}
            isConnectable={conn.fromNode?.id !== id}
          />
          <label htmlFor='text'>
            {isModified && '[Modified] '}
            {!isClean && '*'}
            {title}
          </label>
        </Box>

        <Box role='button' onClick={toggleEdit} onKeyDown={toggleEdit}>
          <Box sx={{ marginLeft: 1, marginRight: 1 }}>
            <Typography>ID: {id}</Typography>
            <Typography>Description: {description || '--'}</Typography>
            {parameterForm && props.renderParameterForm?.(parameterForm.formState.defaultValues, parameter)}
            {!parameterForm && props.renderParameter?.(parameter)}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              marginTop: 1,
              marginBottom: 1,
            }}>
            {initialSourceHandles && initialSourceHandles.length > 0 && (
              <>
                <Divider />
                {initialSourceHandles.map(({ handleId, selector }, _, handles) => {
                  // TODO: custom selector component
                  function Selector() {
                    if (selector) {
                      if (selector.class in SelectorTypes) {
                        const Selector = SelectorTypes[selector.class as ParameterClass];
                        return <Selector {...selector} />;
                      }
                      return <DefaultSelector {...selector} />;
                    }
                    return <BaseSelector>{handles.length > 1 ? 'No Match' : 'Success'}</BaseSelector>;
                  }
                  const hConn = getHandleConnections({
                    type: 'source',
                    nodeId: id,
                    id: handleId,
                  });
                  const noConnection = hConn.length < 1;
                  return (
                    <Box sx={{ position: 'relative' }} key={handleId}>
                      <Selector />
                      <Handle
                        id={handleId}
                        type='source'
                        position={Position.Right}
                        style={{
                          width: 14,
                          height: 14,
                          background: (
                            conn.inProgress
                              ? conn.fromHandle.id === handleId || (noConnection && conn.fromHandle.type == 'target')
                              : noConnection
                          )
                            ? blue[600]
                            : grey[500],
                        }}
                        isConnectable={noConnection}
                      />
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
